import React, { useState, useEffect } from 'react';
import { TrendingUp, ChevronDown, ChevronUp, Calendar, ArrowLeft, Gift } from 'lucide-react';
import { Link } from 'react-router-dom';
import { FileIndicator } from '../components/common/FileIndicator';
import Navigation from '../components/Navigation/Navigation';
import apiClient from '../utils/apiClient';

interface MostUsedSong {
  song_id: number;
  title: string;
  composer_name?: string;
  usage_count: number;
  has_file?: boolean;
}

interface MostUsedSongsResponse {
  songs: MostUsedSong[];
}

interface SongUsageDetail {
  service_id: number;
  liturgy_date: string;
  liturgy_name: string;
  special_service_name?: string;
  song_placement: string;
}

interface SongUsageHistoryResponse {
  song_id: number;
  title: string;
  usage_details: SongUsageDetail[];
}

interface MostUsedSongsPageProps {
  hideNavigation?: boolean;
}

const MostUsedSongsPage: React.FC<MostUsedSongsPageProps> = ({ hideNavigation = false }) => {
  const [mostUsedSongs, setMostUsedSongs] = useState<MostUsedSong[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedSongs, setExpandedSongs] = useState<Set<number>>(new Set());
  const [songHistories, setSongHistories] = useState<Map<number, SongUsageDetail[]>>(new Map());
  const [loadingHistories, setLoadingHistories] = useState<Set<number>>(new Set());
  const [excludeChristmas, setExcludeChristmas] = useState(false);

  useEffect(() => {
    const fetchMostUsedSongs = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get(`/song-selections/most-used?limit=50&excludeChristmas=${excludeChristmas}`);
        const data: MostUsedSongsResponse = response.data;
        setMostUsedSongs(data.songs);
        setError(null);
      } catch (error) {
        console.error('Error fetching most used songs:', error);
        setError('Failed to load most used songs');
      } finally {
        setLoading(false);
      }
    };

    fetchMostUsedSongs();
    // Clear song histories when exclude Christmas setting changes
    setSongHistories(new Map());
    setExpandedSongs(new Set());
  }, [excludeChristmas]);

  const toggleExcludeChristmas = () => {
    setExcludeChristmas(!excludeChristmas);
  };

  const toggleSongExpansion = async (songId: number) => {
    const isExpanded = expandedSongs.has(songId);
    
    if (isExpanded) {
      // Collapse
      const newExpanded = new Set(expandedSongs);
      newExpanded.delete(songId);
      setExpandedSongs(newExpanded);
    } else {
      // Expand and fetch history if not already fetched
      const newExpanded = new Set(expandedSongs);
      newExpanded.add(songId);
      setExpandedSongs(newExpanded);

      if (!songHistories.has(songId)) {
        // Fetch usage history
        const newLoadingHistories = new Set(loadingHistories);
        newLoadingHistories.add(songId);
        setLoadingHistories(newLoadingHistories);

        try {
          const response = await apiClient.get(`/song-selections/song/${songId}/usage-history?excludeChristmas=${excludeChristmas}`);
          const data: SongUsageHistoryResponse = response.data;
          
          setSongHistories(prev => {
            const newMap = new Map(prev);
            newMap.set(songId, data.usage_details);
            return newMap;
          });
        } catch (error) {
          console.error('Error fetching song usage history:', error);
        } finally {
          setLoadingHistories(prev => {
            const newSet = new Set(prev);
            newSet.delete(songId);
            return newSet;
          });
        }
      }
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const day = date.getDate();
    return `${month} ${day}, ${year}`;
  };

  if (loading) {
    return (
      <>
        {!hideNavigation && <Navigation />}
        <div className="container mt-4">
          <div className="d-flex align-items-center justify-content-between mb-4">
            <div className="d-flex align-items-center">
              <Link to="/settings" className="btn btn-outline-secondary me-3">
                <ArrowLeft size={16} className="me-1" />
                Back to Settings
              </Link>
              <TrendingUp className="me-2" size={32} />
              <h1>Most Used Songs</h1>
            </div>
            <button
              className={`btn ${excludeChristmas ? 'btn-warning' : 'btn-outline-warning'}`}
              onClick={toggleExcludeChristmas}
              title={excludeChristmas ? "Click to include Christmas services" : "Click to exclude Christmas services"}
            >
              <Gift size={16} className="me-2" />
              {excludeChristmas ? 'Christmas Excluded' : 'Include Christmas'}
            </button>
          </div>
          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-center">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        {!hideNavigation && <Navigation />}
        <div className="container mt-4">
          <div className="d-flex align-items-center justify-content-between mb-4">
            <div className="d-flex align-items-center">
              <Link to="/settings" className="btn btn-outline-secondary me-3">
                <ArrowLeft size={16} className="me-1" />
                Back to Settings
              </Link>
              <TrendingUp className="me-2" size={32} />
              <h1>Most Used Songs</h1>
            </div>
            <button
              className={`btn ${excludeChristmas ? 'btn-warning' : 'btn-outline-warning'}`}
              onClick={toggleExcludeChristmas}
              title={excludeChristmas ? "Click to include Christmas services" : "Click to exclude Christmas services"}
            >
              <Gift size={16} className="me-2" />
              {excludeChristmas ? 'Christmas Excluded' : 'Include Christmas'}
            </button>
          </div>
          <div className="card">
            <div className="card-body">
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {!hideNavigation && <Navigation />}
      <div className="container mt-4">
        <div className="d-flex align-items-center justify-content-between mb-4">
          <div className="d-flex align-items-center">
            <Link to="/settings" className="btn btn-outline-secondary me-3">
              <ArrowLeft size={16} className="me-1" />
              Back to Settings
            </Link>
            <TrendingUp className="me-2" size={32} />
            <h1>Most Used Songs</h1>
          </div>
          <button
            className={`btn ${excludeChristmas ? 'btn-warning' : 'btn-outline-warning'}`}
            onClick={toggleExcludeChristmas}
            title={excludeChristmas ? "Click to include Christmas services" : "Click to exclude Christmas services"}
          >
            <Gift size={16} className="me-2" />
            {excludeChristmas ? 'Christmas Excluded' : 'Include Christmas'}
          </button>
        </div>

        <div className="card">
          <div className="card-header">
            <h3 className="mb-1">
              Song Usage Statistics
            </h3>
            <small className="text-muted">
              Songs ranked by how often they appear in service planning (excludes Service music{excludeChristmas ? ' and Christmas services' : ''}). Click any song to see usage history.
            </small>
          </div>
          <div className="card-body p-0">
            {mostUsedSongs.length === 0 ? (
              <div className="p-3 text-center text-muted">
                No song usage data available
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th scope="col" className="text-center">#</th>
                      <th scope="col">Song Title</th>
                      <th scope="col">Composer</th>
                      <th scope="col" className="text-center">Times Used</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mostUsedSongs.map((song, index) => {
                      const isExpanded = expandedSongs.has(song.song_id);
                      const history = songHistories.get(song.song_id) || [];
                      const isLoadingHistory = loadingHistories.has(song.song_id);
                      
                      return (
                        <React.Fragment key={song.song_id}>
                          <tr 
                            className="cursor-pointer"
                            onClick={() => toggleSongExpansion(song.song_id)}
                            style={{ cursor: 'pointer' }}
                          >
                            <td className="text-center">
                              <span className={`badge ${index < 3 ? 'bg-warning' : index < 10 ? 'bg-info' : 'bg-secondary'}`}>
                                {index + 1}
                              </span>
                            </td>
                            <td>
                              <div className="d-flex align-items-center">
                                {isExpanded ? <ChevronUp size={16} className="me-2" /> : <ChevronDown size={16} className="me-2" />}
                                <strong><FileIndicator hasFile={song.has_file} />{song.title}</strong>
                              </div>
                            </td>
                            <td className="text-muted">
                              {song.composer_name || 'Unknown'}
                            </td>
                            <td className="text-center">
                              <span className="badge bg-primary">
                                {song.usage_count}
                              </span>
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr>
                              <td colSpan={4} className="bg-light">
                                {isLoadingHistory ? (
                                  <div className="d-flex justify-content-center p-3">
                                    <div className="spinner-border spinner-border-sm" role="status">
                                      <span className="visually-hidden">Loading...</span>
                                    </div>
                                  </div>
                                ) : history.length === 0 ? (
                                  <div className="p-3 text-center text-muted">
                                    No usage history found
                                  </div>
                                ) : (
                                  <div className="p-3">
                                    <h6 className="mb-3">
                                      <Calendar size={16} className="me-2" />
                                      Usage History ({history.length} services)
                                    </h6>
                                    <div className="row">
                                      {history.map((usage, idx) => (
                                        <div key={`${usage.service_id}-${idx}`} className="col-md-6 col-lg-4 col-xl-3 mb-3">
                                          <div className="small border rounded p-2 bg-white">
                                            <div className="fw-bold">
                                              {usage.special_service_name || usage.liturgy_name}
                                            </div>
                                            <div className="text-muted">
                                              {formatDate(usage.liturgy_date)}
                                            </div>
                                            {usage.song_placement && (
                                              <div className="text-primary small">
                                                <strong>Placement:</strong> {usage.song_placement}
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default MostUsedSongsPage;